<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0 d-flex align-items-center justify-content-center">
      <!-- Login-->
      <b-col
        xs="12"
        sm="8"
        md="6"
        lg="4"
        class="d-flex align-items-center card p-5"
      >
        <ds-breeze-logo
          class="auth-logo"
          :height="28"
        />
        <!-- form -->
        <b-col>
          <h1 class="auth-title sr-only">
            Sign in
          </h1>

          <ds-note
            v-if="errorMessage"
            ref="note"
            variant="danger"
            live="alert"
          >
            <strong>{{ errorMessage.title }}</strong><br>
            {{ errorMessage.detail }}
          </ds-note>

          <validation-observer ref="loginValidation">
            <b-form
              class="auth-login-form mt-2"
              @submit.prevent="handleSubmit"
            >
              <!-- username: type="text", not "email". Partner usernames are not all email
                   addresses, and type="email" made the browser refuse to submit one that
                   wasn't, with its own "Please include an '@'" bubble we can't reword. -->
              <div class="auth-field">
                <label
                  class="auth-label"
                  for="login-username"
                >Username</label>
                <validation-provider
                  #default="{ errors }"
                  name="Username"
                  rules="required"
                >
                  <ds-input
                    id="login-username"
                    v-model.trim="username"
                    type="text"
                    name="login-username"
                    autocomplete="username"
                    placeholder="Your username"
                    :invalid="errors.length > 0"
                    :aria-describedby="errors.length > 0 ? 'login-username-error' : null"
                  />
                  <div
                    id="login-username-error"
                    class="auth-error"
                    role="alert"
                  >
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <!-- password -->
              <validation-provider
                #default="{ errors }"
                name="Password"
                rules="required"
              >
                <ds-password-input
                  id="login-password"
                  v-model="password"
                  label="Password"
                  placeholder="Your password"
                  autocomplete="current-password"
                  :error="errors[0]"
                >
                  <template #label-aside>
                    <b-link :to="{name:'auth-forgot-password-v2'}">
                      Forgot password?
                    </b-link>
                  </template>
                </ds-password-input>
              </validation-provider>

              <!-- submit -->
              <!-- Disabled in flight: three clicks in a second used to mean three requests. -->
              <ds-button
                type="submit"
                class="auth-submit"
                :disabled="loggingIn"
              >
                {{ loggingIn ? 'Signing in…' : 'Sign in' }}
              </ds-button>
            </b-form>
          </validation-observer>
        </b-col>
      </b-col>
    <!-- /Login-->
    </b-row>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BRow, BCol, BLink, BForm,
} from 'bootstrap-vue'
import {
  DsBreezeLogo, DsButton, DsInput, DsNote, DsPasswordInput,
} from '@/design-system/components'
import { required } from '@validations'

// How long the sign-in failure stays on screen before clearing itself.
const ERROR_VISIBLE_MS = 5000

const FAILURE_TITLE = 'Sorry, we couldn’t sign you in.'

// requests.handleResponse rejects with the parsed response body, so a server-supplied
// message is the accurate one. A thrown Error means the request never produced a body
// (offline fetch, unparseable response), where advice about credentials would be wrong.
function failureMessage(error) {
  const fromServer = error && !(error instanceof Error) ? error.message : null
  return {
    title: FAILURE_TITLE,
    detail: fromServer || 'Please check your details and try again.',
  }
}

export default {
  components: {
    DsBreezeLogo,
    DsButton,
    DsInput,
    DsNote,
    DsPasswordInput,
    BRow,
    BCol,
    BLink,
    BForm,
    ValidationProvider,
    ValidationObserver,
  },
  data() {
    return {
      password: '',
      username: '',
      // Shown inline in the form; a toast for this stacked one per attempt and sat far
      // from the button that caused it.
      errorMessage: null,
      errorTimer: null,
      required,
    }
  },
  computed: {
    loggingIn() {
      return !!this.$store.state.authentication.status.loggingIn
    },
  },
  beforeDestroy() {
    this.clearError()
  },
  created() {
    // reset login status
    this.$store.dispatch('authentication/logout')

    // pre-fill username if redirected from setup-password
    if (this.$route.query.username) {
      this.username = this.$route.query.username
    }
  },
  methods: {
    showError(message) {
      this.errorMessage = message
      // Restart the countdown so a fresh failure gets its full five seconds.
      if (this.errorTimer) clearTimeout(this.errorTimer)
      this.errorTimer = setTimeout(() => {
        this.errorMessage = null
        this.errorTimer = null
      }, ERROR_VISIBLE_MS)
      // role="alert" covers screen readers; this is for magnifier users, whose viewport
      // may not include the note. Scroll rather than focus, so the message isn't
      // announced twice and the button keeps focus for a retry.
      this.$nextTick(() => {
        const note = this.$refs.note && this.$refs.note.$el
        if (note && note.scrollIntoView) {
          note.scrollIntoView({ block: 'nearest' })
        }
      })
    },
    clearError() {
      this.errorMessage = null
      if (this.errorTimer) {
        clearTimeout(this.errorTimer)
        this.errorTimer = null
      }
    },
    async handleSubmit() {
      // Validate first: an empty submit used to clear the previous failure and then no-op,
      // leaving the form with no feedback at all.
      const valid = await this.$refs.loginValidation.validate()
      if (!valid || this.loggingIn) {
        return
      }

      this.clearError()
      const { username, password } = this
      try {
        await this.$store.dispatch('authentication/login', { username, password })
      } catch (error) {
        // Replaces the message rather than stacking one per attempt.
        this.showError(failureMessage(error))
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';
</style>

<style lang="scss" scoped>
@import './auth-form';
</style>
